
import React, { memo, useEffect, useState } from "react";
import { Profile } from "@/hooks/designer-grid/types";
import ProfileCard from "./ProfileCard";
import LoadingState from "./LoadingState";
import ErrorState from "./ErrorState";
import EmptyState from "./EmptyState";
import PaginationControls from "./PaginationControls";
import { ContentPagination } from "@/v2/services/types/serviceInterfaces";

interface GridContentProps {
  profiles: Profile[];
  loading: boolean;
  error: string | null;
  pagination: ContentPagination;
  hasFilters: boolean;
  category: string;
  onRetry: () => void;
  resetFilters: () => void;
  onPageChange?: (page: number) => void;
}

// Memoize the ProfileCard component to prevent unnecessary re-renders
const MemoizedProfileCard = memo(ProfileCard);

const GridContent = ({
  profiles,
  loading,
  error,
  pagination,
  hasFilters,
  category,
  onRetry,
  resetFilters,
  onPageChange,
}: GridContentProps) => {
  const [localProfiles, setLocalProfiles] = useState<Profile[]>(profiles);
  const [localLoading, setLocalLoading] = useState<boolean>(loading);
  const [localPagination, setLocalPagination] =
    useState<ContentPagination>(pagination);

  useEffect(() => {
    setLocalProfiles(profiles);
    setLocalLoading(loading);
    setLocalPagination(pagination);
  }, [profiles, loading, pagination]);

  // Sort profiles so the first card is the one with the most recently added/updated portfolio item
  const sortedProfiles = [...localProfiles].sort((a, b) => {
    // Get the most recent valid portfolio item date for each profile
    const getLatestPortfolioDate = (profile) => {
      if (!profile.portfolio_items || profile.portfolio_items.length === 0) return 0;
      const validDates = profile.portfolio_items
        .map(item => item.updated_at || item.created_at)
        .filter(dateStr => dateStr && !isNaN(new Date(dateStr).getTime()));
      if (validDates.length === 0) return 0;
      const maxDate = Math.max(...validDates.map(dateStr => new Date(dateStr).getTime()));
      // Debug log
      console.log('Profile:', profile.display_name || profile.username, 'Latest portfolio date:', new Date(maxDate).toISOString());
      return maxDate;
    };
    return getLatestPortfolioDate(b) - getLatestPortfolioDate(a);
  });

  return (
    <>
      {localLoading && <LoadingState />}
      {!localLoading && localProfiles.length === 0 && (
        <EmptyState
          hasFilters={hasFilters}
          category={category}
          onResetFilters={resetFilters}
        />
      )}
      {!localLoading && localProfiles.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {sortedProfiles.map((profile) => (
              <MemoizedProfileCard key={profile.id} profile={profile} />
            ))}
          </div>
          <PaginationControls
            pagination={localPagination}
            onPageChange={onPageChange}
          />
        </>
      )}
    </>
  );
};
export default memo(GridContent);
